import { ArrowRight, Star } from '@phosphor-icons/react';
import { Button, VText } from '@vake/ui-kit';
import { FC, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getRequestHeaders, vake_api_server_url } from '../requests/helpers';
import { markAsInteresting, updatePFDescription, updatePFReportName } from '../requests/reports';
import { formatStartEnd, formatUtc } from '../shared/components/time';
import { InputFieldTW } from '../shared/styles/InputFields';
import { useDebounce } from '../utils/debounce';
import { mmsiToFlag } from '../utils/mmsi-to-flag';
import { checkIfValidUUID } from '../utils/validation';
import { Loading } from './loading/loading';
import { CenteredElement } from './Report/styles/Wrappers';

interface PathfinderReport {
    id: string;
    url?: string;
    bucket_path?: string;
    mmsi?: string;
    uuid?: string;
    mark?: string;
    from?: string;
    to?: string;
    name?: string;
    username?: string;
    created_at?: string;
    report_name?: string;
    description?: string;
}
interface DateIntervals {
    from: string;
    to: string;
}

interface PathfinderDetails {
    url: string;
    dateInterval: DateIntervals;
    uuid?: string;
    mark?: string;
    customer_group_name?: string;
    user_name?: string;
    created_at?: string;
    report_name: string;
    description: string;
}
interface PathfindersDict {
    [mmsi: string]: PathfinderDetails[];
}
interface MarkReportProps {
    index: number;
    mmsi: string;
    reportUUID?: string;
    currMark?: string;
    description: string;
    reportName: string;
}

const useGetUserPathfinderSubscriptions = () => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/dashboard/get-pathfinder-subscriptions`;
    return useQuery<PathfinderReport[]>(['pathfinder_reports'], async () => {
        const res = await fetch(url, {
            credentials: 'include',
            headers: getRequestHeaders(token),
        });

        if (!res.ok) {
            throw new Error(res.statusText);
        }

        return await res.json();
    });
};

const returnValidURL = (Url: string | undefined, bucketPath: string | undefined) => {
    if (Url) {
        return Url;
    } else if (bucketPath) {
        return bucketPath;
    }
    return 'no url';
};
const returnListOfPFValues = (id: string | undefined, bucketPath: string | undefined) => {
    if (id) {
        return id.split('_');
    } else if (bucketPath) {
        return bucketPath.split('_');
    }
    return [];
};

const compareReportTimestamps = (a: PathfinderDetails, b: PathfinderDetails): number => {
    const a_from = new Date(a.dateInterval.from);
    const b_from = new Date(b.dateInterval.from);
    if (a_from < b_from) {
        return -1;
    } else if (a_from > b_from) {
        return 1;
    }
    if (new Date(a.dateInterval.to) < new Date(b.dateInterval.to)) {
        return -1;
    } else {
        return 1;
    }
};

const addToDict = (
    addTo: PathfindersDict | null,
    mmsi: string,
    pathfinderObject: PathfinderDetails
): PathfindersDict | null => {
    if (addTo) {
        const newData = JSON.parse(JSON.stringify(addTo)); // Deep copy
        if (mmsi in newData) {
            const sameMMSIReports = newData[mmsi];
            sameMMSIReports.push(pathfinderObject);
            sameMMSIReports.sort(compareReportTimestamps);
            newData[mmsi] = sameMMSIReports;
        } else {
            newData[mmsi] = [pathfinderObject];
        }
        return newData;
    }
    return addTo;
};

const removeFromDict = (
    removeFrom: PathfindersDict | null,
    mmsi: string,
    index: number,
    setHasFlaggedData: (value: boolean) => void
): PathfindersDict | null => {
    if (removeFrom) {
        const newData = JSON.parse(JSON.stringify(removeFrom)); // Deep copy
        if (newData[mmsi].length < 2) {
            delete newData[mmsi];
        } else {
            newData[mmsi].splice(index, 1);
        }
        if (Object.keys(newData).length === 0) {
            setHasFlaggedData(false);
        }
        return newData;
    }
    return removeFrom;
};

// TODO: In the future this class and OverwatchReports can be shared
const PathfinderReports: FC = () => {
    const { token } = useAuth();
    const { data: pathfinderReportItems, isLoading, isError } = useGetUserPathfinderSubscriptions();
    const [cleanPathfinderData, setCleanPathfinderData] = useState<PathfindersDict | null>(null);
    const [flaggedPathfinderData, setFlaggedPathfinderData] = useState<PathfindersDict | null>(null);
    const [hasFlaggedData, setHasFlaggedData] = useState<boolean>(false);

    useEffect(() => {
        if (pathfinderReportItems) {
            const tempCleanedPathfinderData: PathfindersDict = {};
            const tempFlaggedPathfinderData: PathfindersDict = {};
            pathfinderReportItems.forEach((item) => {
                const current_url: string = returnValidURL(item.id, item.bucket_path);
                const vesselInfo: string[] = returnListOfPFValues(item.id, item.bucket_path);
                if (current_url !== 'no url' && vesselInfo.length > 0) {
                    const mmsi = vesselInfo[0];
                    const singlePathfinder = {
                        url: window.location.origin + '/pathfinder/' + current_url,
                        dateInterval: { from: vesselInfo[1], to: vesselInfo[2] },
                        uuid: item.uuid,
                        mark: item.mark,
                        customer_group_name: item.name,
                        user_name: item.username,
                        created_at: item.created_at,
                        report_name: item.report_name ? item.report_name : '',
                        description: item.description ? item.description : '',
                    };
                    if (singlePathfinder.mark === 'important') {
                        // Handle flagged reports
                        setHasFlaggedData(true);
                        if (mmsi in tempFlaggedPathfinderData) {
                            tempFlaggedPathfinderData[mmsi].push(singlePathfinder);
                        } else {
                            tempFlaggedPathfinderData[mmsi] = [singlePathfinder];
                        }
                    } else {
                        if (mmsi in tempCleanedPathfinderData) {
                            tempCleanedPathfinderData[mmsi].push(singlePathfinder);
                        } else {
                            tempCleanedPathfinderData[mmsi] = [singlePathfinder];
                        }
                    }
                }
            });
            setFlaggedPathfinderData(tempFlaggedPathfinderData);
            setCleanPathfinderData(tempCleanedPathfinderData);
        }
    }, [pathfinderReportItems]);

    const markSelectedReport = ({ index, mmsi, reportName, reportUUID, currMark, description }: MarkReportProps) => {
        if (reportUUID && token && checkIfValidUUID(reportUUID) && flaggedPathfinderData && cleanPathfinderData) {
            const newMark = currMark === 'important' ? null : 'important';
            markAsInteresting(reportUUID, newMark, token);
            if (currMark === 'important') {
                const pathfinderObject = flaggedPathfinderData[mmsi][index];
                pathfinderObject.report_name = reportName;
                pathfinderObject.description = description;
                pathfinderObject.mark = undefined;
                setCleanPathfinderData(addToDict(cleanPathfinderData, mmsi, pathfinderObject));
                setFlaggedPathfinderData(removeFromDict(flaggedPathfinderData, mmsi, index, setHasFlaggedData));
            } else {
                const pathfinderObject = cleanPathfinderData[mmsi][index];
                pathfinderObject.report_name = reportName;
                pathfinderObject.description = description;
                pathfinderObject.mark = 'important';
                setFlaggedPathfinderData(addToDict(flaggedPathfinderData, mmsi, pathfinderObject));
                setCleanPathfinderData(removeFromDict(cleanPathfinderData, mmsi, index, setHasFlaggedData));
                setHasFlaggedData(true);
            }
        }
    };

    if (isError) {
        return (
            <div className="flex items-center justify-center h-screen w-fill bg-background-primary">
                <VText>Fetching data failed</VText>
            </div>
        );
    }
    if (isLoading) {
        return <Loading></Loading>;
    }
    return (
        <div className="flex flex-col items-center pt-16 gap-16 max-w-[1536px] mx-auto">
            {flaggedPathfinderData && hasFlaggedData ? (
                <>
                    <VText variant="H2">Starred Reports</VText>
                    <PathfinderReportsBlock
                        pathfinderData={flaggedPathfinderData}
                        token={token}
                        markSelectedReport={markSelectedReport}
                    />
                </>
            ) : null}
            {cleanPathfinderData ? (
                <>
                    {hasFlaggedData ? <VText variant="H2">Other Reports</VText> : null}
                    <PathfinderReportsBlock
                        pathfinderData={cleanPathfinderData}
                        token={token}
                        markSelectedReport={markSelectedReport}
                    />
                </>
            ) : null}
            {!flaggedPathfinderData && !cleanPathfinderData ? (
                <CenteredElement>We found no data for this user</CenteredElement>
            ) : null}
        </div>
    );
};

const PathfinderReportsBlock: FC<{
    pathfinderData: PathfindersDict;
    token: null | string | undefined;
    markSelectedReport: (props: MarkReportProps) => void;
}> = ({ pathfinderData, markSelectedReport, token }) => {
    return (
        <table className="border-separate border-spacing-x-6 border-spacing-y-3 w-full">
            <thead>
                <tr>
                    <th>
                        <VText>MMSI</VText>
                    </th>
                    <th>
                        <VText>Name</VText>
                    </th>
                    <th>
                        <VText>From - To</VText>
                    </th>
                    <th>
                        <VText>Created by</VText>
                    </th>
                    <th>
                        <VText>Description</VText>
                    </th>
                    <th>
                        <VText>Favorite</VText>
                    </th>
                    <th>
                        <VText>Go to Report</VText>
                    </th>
                </tr>
            </thead>
            <tbody>
                {Object.entries(pathfinderData).map(([mmsi, v]) => {
                    return v.map((details, index) => (
                        <tr key={`${mmsi}-${details.created_at}`}>
                            <td>
                                <div className="flex items-center gap-1">
                                    <VText>{mmsiToFlag(mmsi)}</VText>
                                    <VText>{mmsi}</VText>
                                </div>
                            </td>
                            <ReportRow
                                details={details}
                                index={index}
                                totalLength={v.length}
                                mmsi={mmsi}
                                token={token}
                                markSelectedReport={markSelectedReport}
                            />
                        </tr>
                    ));
                })}
            </tbody>
        </table>
    );
};

const ReportRow: FC<{
    details: PathfinderDetails;
    index: number;
    totalLength: number;
    mmsi: string;
    token: null | string | undefined;
    markSelectedReport: (props: MarkReportProps) => void;
}> = ({ details, index, totalLength, mmsi, markSelectedReport, token }) => {
    const [description, setDescription] = useState<string>(details.description);
    const [reportName, setReportName] = useState<string>(details.report_name);

    const updateReportDescription = (newReportDescription?: string, reportUUID?: string) => {
        if (reportUUID && typeof newReportDescription === 'string') {
            updatePFDescription(newReportDescription, reportUUID, token);
        }
    };

    const updateReportName = (reportName?: string, reportUUID?: string) => {
        if (reportUUID && typeof reportName === 'string') {
            updatePFReportName(reportName, reportUUID, token);
        }
    };

    const debounceDescriptionHandler = useDebounce(
        (newDescription) => updateReportDescription(newDescription, details.uuid),
        500
    );
    const debounceReportNameHandler = useDebounce(
        (newReportName) => updateReportName(newReportName, details.uuid),
        500
    );
    return (
        <>
            <td>
                <InputFieldTW
                    value={reportName}
                    onChange={(e) => {
                        setReportName(e.target.value);
                        debounceReportNameHandler(e.target.value);
                    }}
                ></InputFieldTW>
            </td>
            <td>
                <VText>{formatStartEnd(details.dateInterval.from, details.dateInterval.to)}</VText>
            </td>
            <td>
                <VText>{details.created_at ? formatUtc(new Date(details.created_at)) : ''}</VText>
                <div>
                    <VText>
                        {details.customer_group_name ? 'By: ' + details.customer_group_name : null}
                        {details.user_name ? ', ' + details.user_name : null}
                    </VText>
                </div>
            </td>
            <td>
                <InputFieldTW
                    placeholder="Description"
                    value={description}
                    onChange={(e) => {
                        setDescription(e.target.value);
                        debounceDescriptionHandler(e.target.value);
                    }}
                />
            </td>
            <td>
                <Button
                    iconAfter={<Star></Star>}
                    variant={details.mark === 'important' ? 'primary' : 'neutral'}
                    onClick={() =>
                        markSelectedReport({
                            index,
                            mmsi,
                            reportUUID: details.uuid,
                            currMark: details.mark,
                            description,
                            reportName,
                        })
                    }
                ></Button>
            </td>
            <td>
                <Link to={details.url}>
                    <Button iconAfter={<ArrowRight></ArrowRight>}></Button>
                </Link>
            </td>
        </>
    );
};

export default PathfinderReports;
